import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { useAuthStore } from '@/store/auth.store'
import { expenseClaimService } from '@/services/expenseClaimService'
import { accountManagementService } from '@/services/accountManagementService'
import type { ExpenseAccountVm } from '@/api/types'
import type { Account } from '@/services/accountManagementService'
import { showDeleteConfirm, showError, showSuccess } from '@/lib/swal'

export const ExpenseClaimTypesList: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [rows, setRows] = useState<ExpenseAccountVm[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)

  useEffect(() => {
    if (!branchId) return
    const loadAccounts = async () => {
      try {
        const list = await accountManagementService.getAllAccounts(branchId)
        setAccounts(list || [])
      } catch {
        setAccounts([])
      }
    }
    void loadAccounts()
  }, [branchId])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await expenseClaimService.getExpenses(branchId, pageNumber, pageSize)
        setRows(res.data || [])
        setTotalRecords(res.totalRecords || 0)
      } catch (e: any) {
        const msg = (e?.response?.data?.message || e?.message || 'Failed to load expense types').toString()
        const isNoData = /not found|no data|no records|no\s+.*\s+found/i.test(msg)
        setRows([])
        setTotalRecords(0)
        if (!isNoData) toast.error(msg)
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, pageNumber, pageSize])

  const accountLabel = (id?: number | null) => {
    const accId = Number(id) || 0
    if (!accId) return '—'
    const acc = accounts.find((a) => Number(a.id || a.accountId) === accId)
    if (!acc) return String(accId)
    const code = acc.accountCode || ''
    const name = acc.accountNameEn || acc.accountName || acc.accountNameAr || ''
    return code && name ? `${code} - ${name}` : name || code || String(accId)
  }

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    return rows.filter((r) => {
      const name = (r.name ?? '').toLowerCase()
      const debit = accountLabel(r.debitAccount).toLowerCase()
      const credit = accountLabel(r.creditAccount).toLowerCase()
      const vatDebit = accountLabel(r.vatDebitAccount).toLowerCase()
      const vatCredit = accountLabel(r.vatCreditAccount).toLowerCase()
      return name.includes(q) || debit.includes(q) || credit.includes(q) || vatDebit.includes(q) || vatCredit.includes(q)
    })
  }, [rows, searchQuery, accounts])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm('Delete', 'Are you sure you want to delete this expense type?')
    if (!confirmed) return
    try {
      await expenseClaimService.deleteExpense(id)
      await showSuccess('Deleted', 'Expense type deleted successfully.')
      const res = await expenseClaimService.getExpenses(branchId, pageNumber, pageSize)
      setRows(res.data || [])
      setTotalRecords(res.totalRecords || 0)
    } catch (e: any) {
      await showError('Delete failed', e?.response?.data?.message ?? e?.message ?? 'Failed to delete.')
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Expense Types" description="Add and edit expense types and their accounts" />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Please select a branch first.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Expense Types"
        description="Add types here. Accounts can be edited later."
        action={{
          label: 'Add Type',
          onClick: () => navigate('/self-services/expense-claims/types/new'),
          icon: Plus,
        }}
      >
        <Button variant="outline" onClick={() => navigate('/self-services/expense-claims')}>
          Expense Claims
        </Button>
      </PageHeader>

      <Card>
        <CardContent className="pt-6">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <ListLoader />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">Type</TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">Debit Account</TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">Credit Account</TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">VAT Debit</TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">VAT Credit</TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary w-28">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                        No data available in table
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow key={row.id} className="border-b hover:bg-primary/5">
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.name || '—'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{accountLabel(row.debitAccount)}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{accountLabel(row.creditAccount)}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{accountLabel(row.vatDebitAccount)}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{accountLabel(row.vatCreditAccount)}</TableCell>
                        <TableCell className="px-4 py-2 text-center">
                          <div className="inline-flex gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-primary hover:bg-primary/10"
                              onClick={() => navigate(`/self-services/expense-claims/types/${row.id}`)}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:bg-destructive/10"
                              onClick={() => handleDelete(row.id)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  Showing {totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1} to{' '}
                  {Math.min(pageNumber * pageSize, totalRecords)} of {totalRecords} entries
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                    disabled={pageNumber <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm">
                    Page {pageNumber} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                    disabled={pageNumber >= totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
